import { useCallback, useEffect, useRef, useState } from 'react'
import { sync, type SyncOutcome } from '@/core/sync/syncEngine'
import { httpTransport } from '@/core/sync/httpTransport'
import { logger } from '@/core/logging/logger'

/**
 * Runs the sync cycle on the triggers from 07-sync-protocol.md section 4.
 *
 * Safari has no Background Sync API, so this only ever runs while the app is
 * open. That is fine for a tracker you touch daily -- open it and it catches up.
 */

const DEBOUNCE_AFTER_MUTATION_MS = 2_000
const INTERVAL_MS = 5 * 60_000

export type SyncStatus = 'idle' | 'syncing' | 'offline' | 'error' | 'unauthorized'

export interface SyncController {
  status: SyncStatus
  lastSyncAt: number | null
  syncNow: () => void
  /** Call after a local write. Debounced, so typing a comment is not a storm. */
  scheduleSync: () => void
}

export function useSync(enabled: boolean): SyncController {
  const [status, setStatus] = useState<SyncStatus>('idle')
  const [lastSyncAt, setLastSyncAt] = useState<number | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const run = useCallback(async () => {
    if (!enabled) return

    if (!navigator.onLine) {
      // Not an error. The app works offline; saying otherwise implies breakage.
      setStatus('offline')
      return
    }

    setStatus('syncing')
    let outcome: SyncOutcome
    try {
      outcome = await sync(httpTransport)
    } catch (err: unknown) {
      logger.error('sync', 'Unexpected sync failure', err)
      setStatus('error')
      return
    }

    switch (outcome.status) {
      case 'ok':
        setStatus('idle')
        setLastSyncAt(Date.now())
        if (outcome.pushed || outcome.pulled || outcome.repaired) {
          logger.info(
            'sync',
            `pushed ${outcome.pushed}, pulled ${outcome.pulled}, repaired ${outcome.repaired}`,
          )
        }
        break
      case 'error':
        setStatus(outcome.unauthorized ? 'unauthorized' : 'error')
        break
      case 'skipped':
        if (outcome.reason !== 'in-flight') setStatus('idle')
        break
    }
  }, [enabled])

  const syncNow = useCallback(() => {
    void run()
  }, [run])

  const scheduleSync = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => void run(), DEBOUNCE_AFTER_MUTATION_MS)
  }, [run])

  // Foreground, interval, and reconnection.
  useEffect(() => {
    if (!enabled) return

    // Deferred rather than called inline. Starting a sync during the mount
    // commit sets state synchronously inside the effect, which cascades an
    // extra render before the first paint -- and there is no reason the very
    // first frame needs to wait on the network.
    const initial = setTimeout(() => void run(), 0)

    const onVisible = () => {
      if (document.visibilityState === 'visible') void run()
    }
    const onOnline = () => void run()

    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('online', onOnline)
    const interval = setInterval(() => void run(), INTERVAL_MS)

    return () => {
      clearTimeout(initial)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('online', onOnline)
      clearInterval(interval)
    }
  }, [enabled, run])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  return { status, lastSyncAt, syncNow, scheduleSync }
}
